import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2 } from "lucide-react";
import {
  sanityClient,
  contactCategoriesQuery,
  type ContactCategory,
} from "../lib/sanity";
import { fetchClient } from "../lib/api-client";
import toast from "react-hot-toast";

const contactFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  email: z.email("Invalid email address"),
  categorySlug: z.string().min(1, "Please select a category"),
  message: z.string().min(1, "Message is required").max(10000),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export function Contact() {
  const [categories, setCategories] = useState<ContactCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
  });

  useEffect(() => {
    sanityClient
      .fetch<ContactCategory[]>(contactCategoriesQuery)
      .then(setCategories)
      .catch(() => toast.error("Failed to load contact categories"))
      .finally(() => setCategoriesLoading(false));
  }, []);

  const onSubmit = async (data: ContactFormValues) => {
    setSubmitLoading(true);
    try {
      const { response } = await fetchClient.POST(
        "/api/public/contact",
        {
          body: {
            name: data.name,
            email: data.email,
            categorySlug: data.categorySlug,
            message: data.message,
          },
        },
      );

      if (!response.ok) {
        toast.error("Something went wrong. Please try again.");
        return;
      }
      toast.success("Thank you. Your message has been sent and we'll get back to you shortly.");
      reset();
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Contact Us</h1>
        <p className="text-gray-600 mt-1">
          If you&apos;d like more information on what we do, Membership,
          Training Courses or have any other queries please fill in the contact
          form below and we&apos;ll get back to you shortly.
        </p>
      </div>

      <div className="w-full">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 w-full">
          {categoriesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  {...register("name")}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="categorySlug"
                  className="block text-sm font-medium text-gray-700"
                >
                  Category
                </label>
                <select
                  id="categorySlug"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  {...register("categorySlug")}
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat.slug}>
                      {cat.category}
                    </option>
                  ))}
                </select>
                {errors.categorySlug && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.categorySlug.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  rows={5}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  {...register("message")}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={submitLoading || categories.length === 0}
                  className="flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
